import { Box, Chip, Grid, Typography } from "@mui/material";
import pluralize from "pluralize";
import { useContext, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import CustomToolTip from "../../../../components/CustomTooltip";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { ControlStatus } from "../../../../utils/constants";
import ChipColor from "../../../remediation/plan/components/ChipColor";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import ControlStatusIndicator from "../../controls/components/ControlStatusIndicator";
import InvolvedEntityChip from "../../controls/components/EntitiesSection/InvolvedEntityChip";
import { getEntitiesLabel } from "../../controls/components/EntitiesSection/utils";
import NISTLabel from "../../controls/components/NISTLabel";
import { ReactComponent as NISTLogo } from "../../controls/components/NISTLogo.svg";
import DetectionLabelIcon from "../../detections/components/DetectionLabelIcon";
import DetectionsTooltip from "../../detections/components/DetectionsTooltip";

export default function MitigationControlsTable({
  mitigatingControls,
  handleCheckedControls = () => {},
  loading,
  onRowClick,
  checkbox = false,
  boxSXProps = {},
  pageSizeOptionsOverride = [5, 10, 20],
  filterColumns = [],
}) {
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const colors = useColors();

  const columns = [
    {
      field: "control_id",
      headerName: "ID",
      flex: 0.1,
      renderCell: (params) => (
        <CustomToolTip title={params.value} placement={"left"}>
          <Typography variant="body2">#{params.value}</Typography>
        </CustomToolTip>
      ),
    },
    {
      field: "name_for_gap_state",
      headerName: "Name",
      flex: 0.3,
      renderCell: (params) => {
        const maxSeverity = params.row?.last_detections
          ? Math.max(
              ...params.row?.last_detections.map(
                (detection) => detection.severity
              )
            )
          : 0;
        return (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
            {detectionsActive && params.row?.is_detected && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width={"100%"}
              >
                <DetectionsTooltip
                  content={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color={colors.common_black}
                        >
                          {params.row?.last_detections.length +
                            pluralize(
                              " Active event",
                              params.row?.last_detections.length
                            ) +
                            "."}
                        </Typography>
                        <Typography variant="caption">
                          Highest Severity:
                        </Typography>
                        <ChipColor
                          value={maxSeverity}
                          type={"controlPriority"}
                          size="small"
                          labelWidth={true}
                        />
                      </Box>
                      <Typography variant="caption">
                        Click to view details
                      </Typography>
                    </Box>
                  }
                >
                  <Box>
                    <DetectionLabelIcon />
                  </Box>
                </DetectionsTooltip>
              </Box>
            )}
            <CustomToolTip title={params.value} placement={"left"}>
              <Typography variant="body2">{params.value}</Typography>
            </CustomToolTip>
          </Box>
        );
      },
    },
    {
      field: "tool",
      headerName: "Tool",
      headerAlign: "center",
      flex: 0.15,
      align: "center",
      renderCell: (params) => {
        return (
          <SvgToIcon
            IconLogo={IntegrationsMapping[params.value?.toLowerCase()]?.logo}
            width="40px"
          />
        );
      },
    },
    {
      field: "nist_function",
      renderHeader: () => <SvgToIcon IconLogo={NISTLogo} width="40px" />,
      flex: 0.1,
      headerAlign: "left",
      renderCell: (params) => {
        return (
          <CustomToolTip title={"NIST CSF: " + params.value} placement={"top"}>
            <Box>
              <NISTLabel nistFunction={params.value} />
            </Box>
          </CustomToolTip>
        );
      },
    },
    {
      field: "severity",
      headerName: "Severity",
      flex: 0.15,
      renderCell: (params) => (
        <CustomToolTip
          title={params.row.is_gap ? "" : "This control is compliant"}
          placement={"top"}
        >
          <Box>
            <ChipColor
              value={params.value}
              type={"controlPriority"}
              disabled={!params.row.is_gap}
            />
          </Box>
        </CustomToolTip>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.17,
      sortComparator: (v1, v2) => {
        const statusOrder = {
          [ControlStatus.GAP]: 4,
          [ControlStatus.COMPLIANT]: 3,
          [ControlStatus.SNOOZED]: 2,
          [ControlStatus.EXCLUDED]: 1,
        };

        const value1 = statusOrder[v1] || 0;
        const value2 = statusOrder[v2] || 0;

        return value1 - value2;
      },
      renderCell: (params) => {
        return <ControlStatusIndicator status={params.row?.status} />;
      },
    },
    {
      field: "affected_entity_counts_by_type",
      headerName: "Affected Entities",
      width: 200,
      disableClickEventBubbling: true,
      renderCell: (params) => {
        const countsByType = params.value;
        if (!countsByType || Object.keys(countsByType).length === 0) {
          return null;
        }

        const entries = Object.entries(countsByType);
        const firstEntry = entries[0];
        const remainingEntries = entries.slice(1);
        const tooltipTitle = remainingEntries
          .map(([type, count]) => getEntitiesLabel(count, type))
          .join(", ");

        // TODO: This will be updated in https://linear.app/breeze-security/issue/DEV-2543/consolidate-entity-chip-components
        return (
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={1}
            sx={{ marginLeft: "10px" }}
            // Make this box unclickable so it won't confuse the user. Because when clicking on the chip, it will open the remediation drawer.
            onClick={(event) => event.stopPropagation()}
          >
            {firstEntry && (
              <Grid item key={firstEntry[0]}>
                <InvolvedEntityChip
                  type={firstEntry[0]}
                  entitiesCount={firstEntry[1]}
                  setSelectedEntities={null}
                  control={params.row}
                  sx={{ height: "24px" }}
                />
              </Grid>
            )}
            {remainingEntries && remainingEntries.length > 0 && (
              <CustomToolTip title={tooltipTitle}>
                <Chip
                  label={`+${remainingEntries.length}`}
                  style={{
                    minWidth: "24px",
                    background: colors.entity_chip_enabled_fill,
                    boxShadow: colors.entity_chip_enabled_shadow,
                    borderRadius: "8px",
                    fontSize: "12px",
                    color: colors.text_primary,
                    cursor: "auto",
                    userSelect: "none",
                    height: "24px",
                  }}
                />
              </CustomToolTip>
            )}
          </Box>
        );
      },
    },
  ];

  const rows = mitigatingControls?.map((row, index) => ({
    id: row.control_id,
    ...row,
  }));

  const [sortModel, setSortModel] = useState([
    {
      field: "status",
      sort: "desc",
    },
    {
      field: "severity",
      sort: "desc",
    },
  ]);

  const displayedColumns =
    filterColumns.length > 0
      ? columns.filter((column) => filterColumns.includes(column.field))
      : columns;

  return (
    <>
      {rows?.length > 0 ? (
        <StyledDataGrid
          checkboxSelection={checkbox}
          sx={{
            cursor: "pointer",
            overflowX: "auto",
          }}
          columns={displayedColumns}
          rows={rows}
          onCellClick={(params, event) => {
            if (params.field !== "__check__") {
              event.stopPropagation();
              const control_id = params.row.control_id;
              onRowClick(control_id);
            }
          }}
          loading={loading}
          initialState={{
            columns: {
              columnVisibilityModel: {
                platform: false,
              },
            },
          }}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          onRowSelectionModelChange={handleCheckedControls}
          boxSXProps={boxSXProps}
          pageSizeOptionsOverride={pageSizeOptionsOverride}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100px",
          }}
        >
          <Typography variant="subtitle2">
            No mitigating controls found
          </Typography>
        </Box>
      )}
    </>
  );
}
